import { useEffect, useState } from 'react';

import { useQuery } from '@tanstack/react-query';
import { AwardIcon, RefreshCcwIcon } from 'lucide-react';
import { useParams } from 'react-router-dom';

import Confetti from '../../../components/pages/customer-portal/confeti.jsx';
import PortalContainer from '../../../components/pages/customer-portal/container.jsx';
import Loading from '../../../components/feedback/loading.jsx';
import { msCatalystApi } from '../../../services/api/ms-catalyst-api.js';
import { useMediaQuery } from '../../../hooks/use-media-query.jsx';
import { cn } from '../../../utils/cn.js';
import { math } from '../../../utils/math.js';
import { sleep } from '../../../utils/sleep.js';

import '../../../styles/raffle.css';
import '../../../styles/customer-portal.css';

const RAFFLE_DURATION = 20;

export function CampaignsRaffle() {
  const { campaignName } = useParams();

  const queryCampaign = useQuery({
    queryKey: ['raffle_campaign', campaignName],
    queryFn: async () => {
      const { data: getCampaign } = await msCatalystApi.get(
        `campaigns/name/${campaignName}`,
      );

      if (!getCampaign?.data) {
        return {};
      }

      if (getCampaign?.data?.winners) {
        getCampaign.data.winners = JSON.parse(getCampaign.data.winners);
      }

      return getCampaign?.data;
    },
  });

  const queryCampaignCoupons = useQuery({
    queryKey: ['raffle_campaign_coupons', campaignName],
    queryFn: async () => {
      const { data: coupons } = await msCatalystApi.get(
        `coupons?coupons.ROWID=not_in:${queryCampaign.data.ROWID}&coupons.status=Válido&limit=20&offset=0`,
      );

      return coupons?.data;
    },
    select: (data) => {
      return data.map((item) => ({ couponId: item.coupons.ROWID }));
    },
    enabled: queryCampaign.isSuccess,
  });

  const [isOpen, setIsOpen] = useState(false);
  const [seconds, setSeconds] = useState(RAFFLE_DURATION);
  const [start, setStart] = useState(false);
  const isDesktop = useMediaQuery('(min-width: 768px)');

  useEffect(() => {
    if (!queryCampaignCoupons.isSuccess) return;

    if (!start) {
      sleep(1000);
      setStart(true);
    }

    if (seconds <= 0) {
      setIsOpen(true);
      return; // Exit early if seconds reach 0 or less
    }

    const countdownInterval = setInterval(() => {
      setSeconds((prevSeconds) => prevSeconds - 1);
    }, 1000);

    // Clean up interval on component unmount or when seconds reach 0
    return () => clearInterval(countdownInterval);
  }, [seconds, queryCampaignCoupons]);

  return (
    <>
      {queryCampaignCoupons.isPending || queryCampaignCoupons.isLoading ? (
        <div className="fixed grid h-full w-full place-items-center">
          <Loading />
        </div>
      ) : (
        <>
          {isOpen && <Confetti />}

          <button
            className="fixed bottom-5 right-5 z-50 rounded-full bg-portal-text p-4 transition-all hover:opacity-80"
            onClick={() => {
              setIsOpen(false);
              setSeconds(5);
              setStart(false);
            }}
          >
            <RefreshCcwIcon className="text-white" />
          </button>

          <PortalContainer
            className="flex items-center justify-center bg-portal-background !pb-6 !pt-6"
            classNameParent="!min-h-auto"
            header={false}
            footer={false}
          >
            <div className="grid grid-cols-11 gap-6 lg:gap-5">
              <div className="col-span-11 flex items-center lg:col-span-5">
                <img
                  className="mx-auto h-[95%] rounded-[40px] object-cover"
                  alt={`Banner da Campanha ${queryCampaign?.data?.name}`}
                  src={queryCampaign?.data?.bannerUrl}
                />
              </div>
              <div className="relative col-span-11 flex flex-col gap-20 lg:col-span-6">
                <div className="flex h-full items-center justify-center pb-24 pt-12 xl:p-0">
                  {!isOpen && start && (
                    <span className="absolute top-24 text-[160px] font-bold leading-none text-portal-primary">
                      {seconds}
                    </span>
                  )}

                  <GiftDesign isOpen={isOpen} className="xl:size-[500px]" />
                  <div className="absolute mt-20 scale-150 transition-all">
                    {!isOpen ? (
                      <CouponDesign
                        scale={isDesktop ? 150 : 100}
                        isOpen={isOpen}
                        winnerCoupon={
                          queryCampaign?.data?.winner?.coupon?.ROWID
                        }
                        coupons={queryCampaignCoupons?.data}
                      />
                    ) : (
                      <div className="flex flex-col gap-4">
                        {queryCampaign?.data?.winners.map((item, index) => (
                          <CouponDesign
                            scale={isDesktop ? 150 : 100}
                            isOpen={isOpen}
                            winnerCoupon={item?.coupon?.ROWID}
                            winnerOrder={math.increment(index, 1)}
                            coupons={queryCampaignCoupons?.data}
                          />
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </PortalContainer>
        </>
      )}
    </>
  );
}

const CouponDesign = ({
  queryCampaign,
  scale = 100,
  isOpen,
  winnerCoupon,
  winnerOrder,
  coupons,
}) => {
  const [currentCoupon, setCurrentCoupon] = useState(winnerCoupon);

  useEffect(() => {
    let interval;

    if (!isOpen && coupons.length > 0) {
      interval = setInterval(() => {
        const randomIndex = Math.floor(Math.random() * coupons.length);
        setCurrentCoupon(coupons[randomIndex].couponId);
      }, 100); // Change coupon every 100ms (really fast)
    } else {
      setCurrentCoupon(winnerCoupon);
    }

    return () => clearInterval(interval); // Clean up the interval on unmount or when `isOpen` changes
  }, [isOpen, coupons, winnerCoupon]);

  /* 
  <span className="text-[8px] font-bold uppercase text-portal-text">
    {winnerOrder}º Vencedor
  </span>
  */

  return (
    <div
      className={cn(
        `relative scale-[${scale}] flex min-w-[250px] max-w-[400px] items-center justify-center overflow-hidden rounded-xl bg-portal-primary px-6`,
      )}
    >
      {/* circles */}
      <div className="absolute -left-4 size-8 rounded-full bg-portal-background"></div>
      <div className="absolute -right-4 size-8 rounded-full bg-portal-background"></div>
      {/* left */}

      <div className="flex flex-col items-center justify-center gap-1 px-4 py-8">
        {winnerCoupon ? (
          <span className="text-[8px] font-bold uppercase text-portal-text">
            Vencedor
          </span>
        ) : null}
        <div className="flex flex-row border-portal-background">
          <div className="flex items-center justify-center rounded-full bg-portal-primary p-2">
            <AwardIcon className="size-4 text-portal-primary brightness-75" />
          </div>
          <div className="flex flex-col">
            <span className="text-xl font-bold text-portal-text">
              {currentCoupon}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

const GiftDesign = ({ isOpen, className }) => {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      className={cn('gift', isOpen ? 'is-open' : 'is-closed', className)}
      viewBox="-180 -200 800 800"
    >
      <path
        d="M425.435 273.698v193.301c0 24.899-20.099 45-45 45h-150v-271h131.4c1.8 0 3.6.3 5.099.899l48.6 17.701c6 2.1 9.901 7.8 9.901 14.099z"
        fill="#c60034"
      />
      <path
        d="M415.534 259.599l-48.6-17.701c-1.5-.599-3.3-.899-5.099-.899h-311.4c-8.401 0-15 6.599-15 15v211c0 24.899 20.099 45 45 45h300c24.901 0 45-20.101 45-45V273.698c0-6.299-3.901-11.999-9.901-14.099z"
        fill="#fc1a40"
      />
      <path d="M185.435 240.999v271h90v-271h-90z" fill="#fcbf29" />
      <g className="gift-top">
        <path
          d="M420.035 97.788c-5.099-20.7-20.4-36.899-41.1-43.5-20.4-6.301-42.301-1.8-58.2 12.599l-34.801 30.601-6.899-45.899c-3.3-21-16.8-38.701-36.899-47.1-3.9-1.501-7.8-2.701-11.7-3.301-16.5-3.3-33.6.3-47.401 10.501-18.3 13.2-27.599 35.4-24.6 57.599 3.301 22.2 18.301 41.1 39.6 48.6l32.401 11.7 39.9 14.7h.3l69.3 25.499c6.899 2.401 14.099 3.602 20.999 3.602 14.702 0 29.101-5.402 40.501-15.601 16.799-15 23.999-38.101 18.599-60z"
          fill="#fe9923"
        />
        <path
          d="M401.435 157.788c-11.4 10.199-25.8 15.601-40.501 15.601-6.899 0-14.099-1.201-20.999-3.602l-69.3-25.499h-.3l-39.9-14.7V1.187c3.9.601 7.8 1.8 11.7 3.301 20.099 8.399 33.6 26.1 36.899 47.1l6.899 45.899 34.801-30.601c15.899-14.399 37.8-18.9 58.2-12.599 20.7 6.601 36 22.8 41.1 43.5 5.401 21.9-1.799 45.001-18.599 60.001z"
          fill="#fe8821"
        />
        <path
          d="M476.735 234.098l-20.4 56.4c-2.401 6-8.101 9.6-14.101 9.6-1.8 0-3.6-.3-5.099-.899l-155.101-56.4-14.099-46.5-37.5 8.399-32.999 7.5-155.101-56.4c-7.8-2.999-11.699-11.7-9-19.2l20.7-56.398c3.9-11.4 12.301-20.402 23.101-25.501s23.099-5.7 34.499-1.5l118.801 43.2 8.101 2.999s32.399 58.801 33.3 58.801c.601 0 13.5-7.2 26.1-14.101 12.597-6.9 25.198-14.099 25.198-14.099l126.599 46.199c11.4 4.2 20.4 12.601 25.499 23.401 5.102 10.799 5.701 23.1 1.502 34.499z"
          fill="#ff3e75"
        />
        <path
          d="M476.735 234.098l-20.4 56.4c-2.401 6-8.101 9.6-14.101 9.6-1.8 0-3.6-.3-5.099-.899l-155.101-56.4-14.099-46.5-37.5 8.399V96.399l8.101 2.999s32.399 58.801 33.3 58.801c.601 0 13.5-7.2 26.1-14.101 12.598-6.9 25.199-14.099 25.199-14.099l126.599 46.199c11.4 4.2 20.4 12.601 25.499 23.401 5.102 10.799 5.701 23.1 1.502 34.499z"
          fill="#fc1a40"
        />
        <path
          d="M238.535 99.398l-8.101 22.2-32.999 90.601 32.999 12.001 37.5 13.5 14.099 5.099 41.102-112.8-84.6-30.601z"
          fill="#fcbf29"
        />
        <path
          fill="#fe9923"
          d="M323.135 129.999l-41.101 112.8-14.099-5.1-37.5-13.5V121.598l8.1-22.2z"
        />
      </g>
      <path fill="#fe9923" d="M230.439 240.999h45v271h-45z" />
    </svg>
  );
};
